import {
  useGetAiWorkspaceRequest,
  useRunScriptVetCompliance,
  useUpdateVetRequestPrompt,
} from "@entities/aiWorkspace";
import { AI_WORKSPACE_QUERY_KEYS } from "@entities/aiWorkspace/lib/constants";
import { useScriptConceptAutosave } from "@features/ScriptConceptEditor/lib/useScriptConceptAutosave";
import { ToastService } from "@shared/lib/services";
import { IScripting } from "@shared/lib/types/briefCampains";
import { IComplianceReport } from "@shared/lib/types/drafting";
import { useEditorDocumentImport } from "@shared/ui/Editor/lib/useEditorDocumentImport";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import Editor from "@shared/ui/Editor/ui";
import { DocumentUploadButton } from "@shared/ui/Editor/ui/DocumentUploadButton";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  ModuleProgress,
  SCRIPT_VET_STEPS,
  vetDetailStep,
} from "../ModuleProgress";
import { aiWorkspaceRequestId } from "../useAiWorkspaceModuleParams";
import { useLocalRequestPrompt } from "../useLocalRequestPrompt";
import { WorkspacePromptEditor } from "../WorkspacePromptEditor";
import { promptToText } from "../WorkspacePromptEditor/promptToText";
import { ScriptComplianceReport } from "./ScriptComplianceReport";
import styles from "./styles.module.scss";

type VetScripting = IScripting & {
  complianceCheckReport?: IComplianceReport;
  complianceCheckGeneratedAt?: string;
  complianceCheckStatus?: string;
  complianceCheckError?: string | null;
};

export function ScriptVetDetail({
  requestId,
  onBack,
}: {
  requestId: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const editorRef = useRef<EditorHandle>(null);
  const { data, isFetching, refetch } = useGetAiWorkspaceRequest(requestId);
  const request = data?.data?.type === "script-vet" ? data.data : undefined;
  const scripting = request?.scripting as VetScripting | undefined;
  const { promptHtml, initialHtml, updatePrompt, contentKey } =
    useLocalRequestPrompt(
      request?.aiWorkspaceId || "",
      "script-vet",
      request?.masterPrompt || "",
      requestId
    );
  const scriptId = scripting?._id || "";
  const complianceStatus = scripting?.complianceCheckStatus;
  const { scheduleSave, pauseAutosaveForMedia, saveAfterMediaUpload } =
    useScriptConceptAutosave({
      scriptId,
      editorRef,
      onSaved: () => {
        queryClient.invalidateQueries({
          queryKey: [AI_WORKSPACE_QUERY_KEYS.REQUEST, requestId],
        });
        queryClient.invalidateQueries({
          queryKey: [AI_WORKSPACE_QUERY_KEYS.REQUESTS],
        });
      },
    });
  const [committedPrompt, setCommittedPrompt] = useState("");
  const [saveVersion, setSaveVersion] = useState(0);
  const { update: saveRequestPrompt, isUpdating } = useUpdateVetRequestPrompt({
    onSuccess: (prompt) => {
      setCommittedPrompt(prompt);
      setSaveVersion((version) => version + 1);
    },
  });
  const { run, isRunning, hasRun } = useRunScriptVetCompliance();
    const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (complianceStatus !== "Pending" && !isRunning) return;
    const timer = window.setInterval(() => {
      void refetch();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [complianceStatus, isRunning, refetch]);
  const documentImport = useEditorDocumentImport({
    editorRef,
    disabled: !scriptId,
    showStatus: (message, tone) => {
      if (tone === "err") ToastService.error(message);
      else ToastService.success(message);
    },
    onContentChange: () => scheduleSave(),
  });

  if (!request && isFetching) {
    return <p className={styles.statusLine}>Loading script check…</p>;
  }
  if (!request) {
    return <p className={styles.statusLine}>Script check not found.</p>;
  }

  const checked = Boolean(
    hasRun ||
      scripting?.complianceCheckGeneratedAt ||
      scripting?.complianceCheckReport
  );
  const savedRequestPrompt = committedPrompt || request.masterPrompt || "";
  const currentPrompt = promptToText(promptHtml).trim();
  const promptChanged =
    currentPrompt.length > 0 &&
    currentPrompt !== promptToText(savedRequestPrompt).trim();
  const promptIdChanged =
    Boolean(request.masterPromptId) &&
    Boolean(scripting?.aiWorkspaceMasterPromptId) &&
    request.masterPromptId !== scripting?.aiWorkspaceMasterPromptId;
  const promptOutdated = checked && (promptChanged || promptIdChanged);
  const runCheck = () =>
    run({ aiWorkspaceRequestId: aiWorkspaceRequestId(request) });

  const scrollToReport = () => {
    window.setTimeout(() => {
      reportRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleRunCheck = () => {
    runCheck();
    scrollToReport();
  };

  return (
    <div className={styles.tab}>
      <ModuleProgress
        steps={SCRIPT_VET_STEPS}
        current={vetDetailStep(
          complianceStatus === "Pending" || isRunning || checked
        )}
      />
      <WorkspacePromptEditor
        moduleId="script-vet"
        aiWorkspaceId={request.aiWorkspaceId}
        label="Request prompt"
        hint="Save it on this check. Re-run after it changes."
        savedPrompt={initialHtml}
        committedPrompt={savedRequestPrompt}
        contentKey={contentKey}
        isSaving={isUpdating}
        saveVersion={saveVersion}
        requestOnly
        onSave={(prompt) =>
          saveRequestPrompt({
            aiWorkspaceRequestId: requestId,
            prompt: prompt,
          })
        }
        onHtmlChange={updatePrompt}
      />
      <button type="button" className={styles.createBtn} onClick={onBack}>
        <Plus size={14} />
        Create new script
      </button>
      <h2 className={styles.detailTitle}>
        {request.scripting?.title?.trim() || "Script check"}
      </h2>
      <div className={styles.sectionHeader}>
        <label className={styles.sectionLabel}>Script</label>
        <DocumentUploadButton
          fileInputRef={documentImport.fileInputRef}
          accept={documentImport.accept}
          disabled={!scriptId || documentImport.isImporting}
          isImporting={documentImport.isImporting}
          onFileInputChange={documentImport.onFileInputChange}
          onClick={documentImport.openFilePicker}
        />
      </div>
      <div className={styles.scriptFrame}>
        <Editor
          key={aiWorkspaceRequestId(request)}
          ref={editorRef}
          initialHtml={request.scripting?.concept || ""}
          compact
          allowDocumentImport
          onChange={() => scheduleSave()}
          onMediaUploadStart={pauseAutosaveForMedia}
          onMediaUploadComplete={saveAfterMediaUpload}
        />
      </div>
      <button
        type="button"
        className={styles.primaryBtn}
        disabled={isRunning || !scriptId}
        onClick={handleRunCheck}
      >
        {isRunning ? <Loader2 size={14} className="spin-animation" /> : null}
        {checked ? "Re-run script check" : "Run script check"}
      </button>
      <div ref={reportRef}>
        <ScriptComplianceReport
          report={scripting?.complianceCheckReport}
          status={complianceStatus}
          error={
            typeof scripting?.complianceCheckError === "string"
              ? scripting.complianceCheckError
              : null
          }
          outdated={promptOutdated}
          isRunning={isRunning}
          rerunDisabled={!scriptId}
          onRerun={handleRunCheck}
        />
      </div>
    </div>
  );
}
